/* eslint-disable */
import React, { useState } from "react";
import "./PayEpayUtilityBillModal.scss";

const PayEpayUtilityBillModal = ({
  isOpen,
  onClose,
  onConfirm,
  billId,
  existingReceiptUrl = null, // New prop for existing receipt
  paymentStatus = null, // NEW: Payment status (paid/failed)
  existingReceiptNote = null, // NEW: Existing receipt note content
  mode = "pay", // "pay" | "upload-invoice"
}) => {
  console.log("🚀 PayEpayUtilityBillModal rendered with isOpen:", isOpen, "billId:", billId, "existingReceipt:", existingReceiptUrl, "status:", paymentStatus, "receiptNote:", existingReceiptNote);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [showReplaceInput, setShowReplaceInput] = useState(false);

  const isInvoiceUpload = mode === "upload-invoice";

  // Check if receipt already exists
  const hasExistingReceipt = !isInvoiceUpload && existingReceiptUrl && existingReceiptUrl !== null;
  
  // NEW: Determine the required action based on backend rules
  const getRequiredAction = () => {
    if (isInvoiceUpload) {
      return "upload-receipt";
    }
    if (paymentStatus === "failed") {
      return "mark-as-paid"; // Always use mark-as-paid for failed payments
    } else if (paymentStatus === "paid") {
      // Check if no receipt note exists OR existing note contains stripe.com
      const hasNoReceiptNote = !existingReceiptNote || existingReceiptNote.trim() === "";
      const containsStripe = existingReceiptNote && existingReceiptNote.includes("stripe.com");
      
      if (hasNoReceiptNote || containsStripe) {
        return "upload-receipt"; // Use upload-receipt endpoint
      }
    }
    return "upload-receipt"; // Default fallback
  };

  const requiredAction = getRequiredAction();
  
  // NEW: Check if file upload is mandatory
  const isFileUploadMandatory = () => {
    if (isInvoiceUpload) {
      return true;
    }
    if (paymentStatus === "failed") {
      return true; // Always mandatory for failed payments
    }
    return false; // Optional for paid status
  };

  const fileUploadMandatory = isFileUploadMandatory();

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleConfirm = () => {
    console.log("🔄 HandleConfirm - Action:", requiredAction, "Status:", paymentStatus, "MandatoryUpload:", fileUploadMandatory);
    
    // NEW: Backend-compliant logic
    if (requiredAction === "mark-as-paid") {
      // Failed payments: Always use mark-as-paid with mandatory file upload
      if (!selectedFile) {
        console.error("❌ File upload is mandatory for failed payments");
        return; // Don't proceed without file
      }
      
      onConfirm({
        billId,
        action: "mark-as-paid",
        receiptFile: selectedFile,
        paymentStatus,
        requiredAction
      });
    } else if (requiredAction === "upload-receipt") {
      // Paid payments: Use upload-receipt (file optional if existing receipt exists)
      if (selectedFile) {
        // User selected a new file
        onConfirm({
          billId,
          action: "upload-receipt",
          receiptFile: selectedFile,
          paymentStatus,
          requiredAction
        });
      } else if (hasExistingReceipt && !fileUploadMandatory) {
        // Use existing receipt (only allowed for paid status)
        onConfirm({
          billId,
          action: "upload-receipt",
          receiptFile: null,
          useExistingReceipt: true,
          paymentStatus,
          requiredAction
        });
      } else {
        console.error("❌ No file selected and no existing receipt available");
        return;
      }
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setFileName("");
    setShowReplaceInput(false);
    onClose();
  };

  const handleReplaceReceipt = () => {
    setShowReplaceInput(true);
  };

  const handleViewReceipt = () => {
    if (hasExistingReceipt) {
      window.open(existingReceiptUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pay-epay-modal-overlay" onClick={handleCancel}>
      <div className="pay-epay-modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="pay-epay-modal-title">
          {isInvoiceUpload
            ? "UPLOAD EPAY INVOICE"
            : paymentStatus === "failed" ? "MARK UTILITY BILL AS PAID" : "PAY SELECTED UTILITY BILL"}
        </h2>
        
        <p className="pay-epay-modal-description">
          {isInvoiceUpload
            ? "Upload the Epay invoice for this utility bill. It will replace the Stripe receipt."
            : paymentStatus === "failed" 
              ? "Mark this utility bill as paid and upload a receipt. This action cannot be reversed."
              : "Make sure that you want to pay this utility bill, because you can't reverse this action."
          }
        </p>
        
        {/* NEW: Show backend action info for debugging */}
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <strong>Action:</strong> {requiredAction} | <strong>Status:</strong> {paymentStatus} | <strong>Mandatory Upload:</strong> {fileUploadMandatory ? 'Yes' : 'No'}
          {existingReceiptNote && (
            <div><strong>Receipt Note:</strong> {existingReceiptNote.substring(0, 50)}...</div>
          )}
        </div>

        <div className="pay-epay-upload-section">
          <label className="pay-epay-upload-label">
            Receipt {fileUploadMandatory ? '*' : '*'}
            {fileUploadMandatory && !isInvoiceUpload && (
              <span style={{ fontSize: '12px', color: '#dc3545', marginLeft: '8px' }}>
                (Mandatory for failed payments)
              </span>
            )}
          </label>
          
          {hasExistingReceipt && !showReplaceInput && !fileUploadMandatory ? (
            // Show existing receipt info
            <div className="pay-epay-existing-receipt">
              <div className="pay-epay-receipt-info">
                <svg 
                  className="pay-epay-receipt-icon" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
                <span className="pay-epay-receipt-text">Receipt already uploaded</span>
              </div>
              <div className="pay-epay-receipt-actions">
                <button 
                  type="button"
                  className="pay-epay-view-btn"
                  onClick={handleViewReceipt}
                >
                  View
                </button>
                <button 
                  type="button"
                  className="pay-epay-replace-btn"
                  onClick={handleReplaceReceipt}
                >
                  Replace
                </button>
              </div>
            </div>
          ) : (
            // Show upload input (either no existing receipt or user wants to replace)
            <div className="pay-epay-upload-container">
              <input
                type="file"
                id="receipt-upload"
                className="pay-epay-file-input"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
              />
              <label htmlFor="receipt-upload" className="pay-epay-upload-button">
                <span className="pay-epay-upload-text">
                  {fileName || (showReplaceInput ? "UPLOAD NEW RECEIPT" : "UPLOAD")}
                </span>
                <svg 
                  className="pay-epay-upload-icon" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </label>
              {showReplaceInput && (
                <button 
                  type="button"
                  className="pay-epay-cancel-replace-btn"
                  onClick={() => setShowReplaceInput(false)}
                >
                  Cancel Replace
                </button>
              )}
            </div>
          )}
        </div>

        <div className="pay-epay-modal-actions">
          <button 
            className="pay-epay-confirm-btn"
            onClick={handleConfirm}
            disabled={
              // Disable if mandatory upload required but no file selected
              (fileUploadMandatory && !selectedFile) ||
              // Disable if no existing receipt and no file selected
              (!hasExistingReceipt && !selectedFile)
            }
          >
            {requiredAction === "mark-as-paid" ? "MARK AS PAID" : isInvoiceUpload ? "UPLOAD" : "CONFIRM"}
          </button>
          <button 
            className="pay-epay-cancel-btn"
            onClick={handleCancel}
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayEpayUtilityBillModal;
